import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../Components/Nav/Navigation.tsx';
import { Footer } from '../../Components/Footer/Footer.tsx';
import './Pricing.css';

interface Tier {
  title: string;
  who: React.ReactNode;
  incl: React.ReactNode[];
  num: string;
  time: string;
  badge?: string;
  bar: { ms: number; me: number; ys: number; ye: number };
  market: string;
  below: string;
}

interface Section {
  id: string;
  title: string;
  tag: string;
  sub: React.ReactNode;
  ticks: string[];
  tiers: Tier[];
}

const sections: Section[] = [
  {
    id: 'web',
    title: 'Websites',
    tag: 'Web only',
    sub: 'Everything here is mobile-friendly by default, loads fast, and comes with a simple editor so you can change your own text and photos without calling us.',
    ticks: ['$0', '$10k', '$20k', '$30k', '$40k'],
    tiers: [
      {
        title: 'Starter Site',
        who: 'One-page or 3–5 pages. For a new business that needs to look real and get found.',
        incl: [
          'Custom design, not a stock template',
          'Contact form that emails you',
          'Google Maps, hours, click-to-call',
          'Google Business Profile setup',
          'Basic SEO so you show up in local search',
        ],
        num: '$1,800–$2,800',
        time: '2–3 weeks',
        bar: { ms: 6.25, me: 12.5, ys: 4.5, ye: 7 },
        market: 'Market $2,500–$5,000',
        below: '≈35% below',
      },
      {
        title: 'Business Site',
        who: '6–12 pages. For an established business that gets real inquiries from its website.',
        incl: [
          'Service pages, team, gallery, blog',
          'Booking or quote-request forms',
          'Reviews and testimonials built in',
          'Edit-it-yourself dashboard + training call',
          "Analytics so you can see what's working",
        ],
        num: '$3,800–$7,500',
        time: '4–6 weeks',
        badge: 'Most popular',
        bar: { ms: 12.5, me: 37.5, ys: 9.5, ye: 18.75 },
        market: 'Market $5,000–$15,000',
        below: '≈40% below',
      },
      {
        title: 'Online Store',
        who: "Sell products or services and take payment. Includes checkout — you don't pay for that separately here.",
        incl: [
          'Checkout, cards, Apple Pay, Google Pay',
          'Up to 50 products loaded for you',
          'Shipping, tax, discount codes',
          'Order and inventory dashboard',
          'Abandoned-cart and receipt emails',
        ],
        num: '$4,800–$11,000',
        time: '5–8 weeks',
        bar: { ms: 12.5, me: 50, ys: 12, ye: 27.5 },
        market: 'Market $5,000–$20,000',
        below: '≈40% below',
      },
      {
        title: 'Custom Web Platform',
        who: (
          <>A website that <em>does</em> something — customer logins, dashboards, scheduling, internal tools.</>
        ),
        incl: [
          'Customer accounts and secure logins',
          'Custom database built around your workflow',
          'Admin panel for your staff',
          'Connects to your CRM, QuickBooks, or POS',
          'Role-based permissions',
        ],
        num: '$10,000–$25,000',
        time: '8–14 weeks',
        bar: { ms: 37.5, me: 100, ys: 25, ye: 62.5 },
        market: 'Market $15,000–$40,000+',
        below: '≈38% below',
      },
    ],
  },
  {
    id: 'mobile',
    title: 'Mobile Apps',
    tag: 'Mobile only',
    sub: "Built once, published to both the Apple App Store and Google Play. We handle the store submissions, the developer accounts, and the review process — that's the part that trips most people up.",
    ticks: ['$0', '$50k', '$100k', '$150k'],
    tiers: [
      {
        title: 'App MVP',
        who: 'One core thing done well. For testing an idea with real users before spending big.',
        incl: [
          'iOS + Android from one codebase',
          'Up to ~8 screens',
          'Sign-up, profiles, one main feature',
          'Published to both app stores',
          'App icon and store listing design',
        ],
        num: '$14,000–$30,000',
        time: '8–12 weeks',
        bar: { ms: 13.33, me: 40, ys: 9.33, ye: 20 },
        market: 'Market $20,000–$60,000',
        below: '≈45% below',
      },
      {
        title: 'Full App',
        who: 'A real product customers use weekly — ordering, booking, loyalty, memberships.',
        incl: [
          '15–25 screens',
          'Accounts, payments, push notifications',
          'Your own backend and admin dashboard',
          'Offline handling and error recovery',
          'Analytics and crash reporting',
        ],
        num: '$30,000–$60,000',
        time: '14–20 weeks',
        bar: { ms: 26.67, me: 66.67, ys: 20, ye: 40 },
        market: 'Market $40,000–$100,000',
        below: '≈40% below',
      },
      {
        title: 'Advanced App',
        who: 'Marketplaces, multi-role apps, live tracking, anything handling money between two parties.',
        incl: [
          'Multiple user types (buyer / seller / driver)',
          'Real-time features: chat, tracking, live status',
          'Split payments and vendor payouts',
          'Native modules where performance matters',
          'Security review before launch',
        ],
        num: '$65,000–$120,000',
        time: '5–8 months',
        bar: { ms: 66.67, me: 100, ys: 43.33, ye: 80 },
        market: 'Market $100,000–$250,000+',
        below: '≈45% below',
      },
    ],
  },
  {
    id: 'bundles',
    title: 'Web + Mobile Together',
    tag: 'Bundled — save 12–18%',
    sub: 'Doing both at once is cheaper than doing them a year apart. One design system, one backend, one set of customer accounts — so your website and your app always show the same prices, the same inventory, and the same customer.',
    ticks: ['$0', '$50k', '$100k', '$150k'],
    tiers: [
      {
        title: 'Launch Bundle',
        who: 'Business site + app MVP. The standard starting point for a service business going digital.',
        incl: [
          'Business Site (6–12 pages)',
          'App MVP on iOS + Android',
          'Shared logins — one account for both',
          'One admin dashboard for everything',
          'Matching brand across web and app',
        ],
        num: '$16,500–$34,000',
        time: '12–16 weeks',
        badge: 'Save ~15%',
        bar: { ms: 18.67, me: 50, ys: 11, ye: 22.67 },
        market: 'Market $28,000–$75,000',
        below: '≈50% below',
      },
      {
        title: 'Commerce Bundle',
        who: 'Sell on the web and in an app, with one shared cart, catalog, and customer list.',
        incl: [
          'Online Store + shopping app',
          'Checkout & payments on both',
          'One inventory — never sell the same item twice',
          'Loyalty, order history, saved cards',
          'Push notifications for promos and order status',
        ],
        num: '$24,000–$52,000',
        time: '16–22 weeks',
        bar: { ms: 30, me: 80, ys: 16, ye: 34.67 },
        market: 'Market $45,000–$120,000',
        below: '≈52% below',
      },
      {
        title: 'Platform Bundle',
        who: 'Custom web platform + full app + AI. For businesses running their operation on the software.',
        incl: [
          'Custom Web Platform + Full App',
          'One AI capability of your choice included',
          'Staff tools and customer tools in one system',
          'Integrations with the software you already pay for',
          '90 days of post-launch support included',
        ],
        num: '$55,000–$110,000',
        time: '6–9 months',
        bar: { ms: 73.33, me: 100, ys: 36.67, ye: 73.33 },
        market: 'Market $110,000–$250,000+',
        below: '≈50% below',
      },
    ],
  },
  {
    id: 'ai',
    title: 'AI Integration',
    tag: 'Add to anything, or buy standalone',
    sub: 'These can be added to a site or app we build, or bolted onto what you already have. All of them run on your own content — your services, your prices, your policies — so the answers are actually right. AI usage is billed at cost, typically $20–$200/month depending on volume.',
    ticks: ['$0', '$12.5k', '$25k', '$37.5k', '$50k'],
    tiers: [
      {
        title: 'AI Chatbot',
        who: 'Answers customer questions 24/7 and captures leads while you sleep. The most common first AI purchase.',
        incl: [
          'Trained on your services, prices, and FAQs',
          'Books appointments and collects contact info',
          "Hands off to a human when it's out of depth",
          'Emails you a transcript of every conversation',
          'Matches your branding',
        ],
        num: '$1,800–$4,500',
        time: '2–3 weeks',
        badge: '+ $75–$250/mo',
        bar: { ms: 6, me: 30, ys: 3.6, ye: 9 },
        market: 'Market $3,000–$15,000',
        below: '≈45% below',
      },
      {
        title: 'AI Assistant on Your Documents',
        who: 'Ask questions across your manuals, contracts, catalogs, or policies and get sourced answers.',
        incl: [
          'Ingests your PDFs, spreadsheets, and internal docs',
          'Cites where each answer came from',
          "Private — your documents aren't used to train anything",
          'Staff-only or customer-facing',
          'Search across everything at once',
        ],
        num: '$8,000–$20,000',
        time: '6–10 weeks',
        bar: { ms: 30, me: 100, ys: 16, ye: 40 },
        market: 'Market $15,000–$50,000',
        below: '≈50% below',
      },
      {
        title: 'AI Features Inside Your Product',
        who: 'AI built into the site or app itself, not bolted on in a chat bubble.',
        incl: [
          'Smart search that understands plain questions',
          'Personalized recommendations',
          'Auto-written product or listing descriptions',
          'Photo recognition and tagging',
          'Summaries of reviews, notes, or tickets',
        ],
        num: '$5,000–$14,000',
        time: '4–8 weeks',
        bar: { ms: 16, me: 60, ys: 10, ye: 28 },
        market: 'Market $8,000–$30,000',
        below: '≈50% below',
      },
      {
        title: 'AI Workflow Automation',
        who: 'Takes repetitive office work off your team — intake, quoting, routing, follow-up.',
        incl: [
          'Reads incoming emails or forms and sorts them',
          'Drafts quotes and replies for you to approve',
          'Pulls data out of invoices and receipts',
          'Automatic follow-up sequences',
          'Connects to the tools you already use',
        ],
        num: '$3,500–$12,000',
        time: '3–7 weeks',
        bar: { ms: 10, me: 50, ys: 7, ye: 24 },
        market: 'Market $5,000–$25,000',
        below: '≈50% below',
      },
    ],
  },
];

const addons: Array<{ feat: string; desc: string; ours: string; market: string }> = [
  { feat: 'Checkout & payments', desc: 'Take cards, Apple Pay, Google Pay. Receipts, refunds, and payouts to your bank.', ours: '$1,200–$3,000', market: '$2,000–$5,000' },
  { feat: 'Subscriptions & recurring billing', desc: 'Memberships, monthly plans, automatic renewals, failed-payment recovery.', ours: '$1,500–$3,800', market: '$1,500–$5,000' },
  { feat: 'Booking & scheduling', desc: 'Customers pick a time, it lands on your calendar, reminders go out automatically.', ours: '$900–$2,400', market: '$1,500–$4,000' },
  { feat: 'Customer accounts & login', desc: 'Sign in with email, Google, or Apple. Order history, saved details, password resets.', ours: '$1,200–$2,800', market: '$2,000–$5,000' },
  { feat: 'Admin dashboard', desc: 'Your own back office — see orders, customers, and bookings without calling us.', ours: '$1,500–$3,800', market: '$2,500–$6,000' },
  { feat: 'Push notifications & SMS', desc: 'Order updates, appointment reminders, promos sent to phones.', ours: '$600–$1,600', market: '$1,000–$3,000' },
  { feat: 'Live chat & support inbox', desc: 'Real-time messaging with your team, all conversations in one place.', ours: '$500–$1,400', market: '$800–$2,500' },
  { feat: 'Reviews & ratings', desc: 'Collect reviews, show them off, push them to your Google profile.', ours: '$600–$1,500', market: '$1,000–$2,500' },
  { feat: 'Maps, locations & delivery zones', desc: 'Store locator, service-area checks, driving directions.', ours: '$800–$2,200', market: '$1,200–$3,500' },
  { feat: 'Software integrations', desc: 'Connect QuickBooks, your CRM, POS, Mailchimp, or industry software.', ours: '$1,200–$4,000', market: '$2,000–$8,000' },
  { feat: 'Second language', desc: 'Full translation of the site or app, per language.', ours: '$900–$2,500', market: '$1,500–$4,000' },
  { feat: 'Marketplace with vendor payouts', desc: 'Multiple sellers, commission splits, automatic payouts. Complex — priced per project.', ours: '$5,000–$12,000', market: '$8,000–$20,000' },
  { feat: 'SEO setup & analytics', desc: 'Get found on Google, and see where your customers actually come from.', ours: '$600–$1,800', market: '$1,000–$3,000' },
  { feat: 'Accessibility (ADA / WCAG) pass', desc: 'Usable with screen readers and keyboards. Reduces legal exposure.', ours: '$900–$2,500', market: '$1,500–$5,000' },
  { feat: 'Copywriting', desc: 'We write the words. Per page.', ours: '$150–$350', market: '$250–$600' },
  { feat: 'Logo & brand basics', desc: 'Logo, colors, fonts, and a one-page style guide.', ours: '$800–$2,200', market: '$1,500–$5,000' },
];

const plans = [
  {
    name: 'Essentials',
    price: '$95',
    pick: false,
    incl: [
      'Hosting, domain, and SSL handled',
      'Daily backups',
      'Security and software updates',
      'Uptime monitoring',
      '30 minutes of edits a month',
    ],
  },
  {
    name: 'Growth',
    price: '$225',
    pick: true,
    incl: [
      'Everything in Essentials',
      '2 hours of edits a month',
      'Speed and SEO check quarterly',
      'Monthly traffic report in plain English',
      'Same-day response',
    ],
  },
  {
    name: 'Store & App Care',
    price: '$495',
    pick: false,
    incl: [
      'Everything in Growth',
      '4 hours of edits a month',
      'App store updates when Apple/Google require them',
      'Checkout and payment monitoring',
      'Emergency line for outages',
    ],
  },
];

const TierRow = ({ tier }: { tier: Tier }) => (
  <article className="tier">
    <div>
      <h3>{tier.title}</h3>
      <p className="who">{tier.who}</p>
    </div>
    <ul className="incl">
      {tier.incl.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
    <div className="price">
      <span className="num">{tier.num}</span>
      <span className="time">{tier.time}</span>
      {tier.badge && <span className="badge">{tier.badge}</span>}
    </div>
    <div className="rangewrap">
      <div
        className="bar"
        style={{ '--ms': tier.bar.ms, '--me': tier.bar.me, '--ys': tier.bar.ys, '--ye': tier.bar.ye } as React.CSSProperties}
      >
        <div className="rail"></div>
        <div className="seg market"></div>
        <div className="seg mine"></div>
      </div>
      <div className="barlabel">
        <span className="mkt">{tier.market}</span>
        <span>{tier.below}</span>
      </div>
    </div>
  </article>
);

export const Pricing = () => {
  const [showMarket, setShowMarket] = useState(true);

  useEffect(() => {
    document.title = 'Rate Card | Hakuna Matata Technologies LLC';
    return () => {
      document.title = 'Hakuna Matata Technologies LLC — Web, Mobile & AI';
    };
  }, []);

  return (
    <>
      <Navigation />
      <main className={`rate${showMarket ? '' : ' client'}`}>
        <div className="wrap">
          <header className="mast">
            <p className="eyebrow">Rate card · Effective July 2026</p>
            <h1>What it costs to build your website, your app, or both.</h1>
            <p className="lede">
              Fixed prices, plain English, no hourly surprises. Every number below is a
              real range — where you land inside it depends on how many pages, screens,
              and moving parts you need. You'll get an exact figure in writing before
              anyone starts building.
            </p>
            <div className="mast-meta">
              <span>Serving NYC &amp; Northern NJ</span>
              <span>Fixed-fee projects</span>
              <span>You own the code</span>
            </div>
          </header>

          <div className="legend">
            <div className="key"><span className="swatch market"></span>Typical NYC / N. NJ agency range</div>
            <div className="key"><span className="swatch mine"></span>Our price</div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={showMarket}
                onChange={(e) => setShowMarket(e.target.checked)}
              />
              <span className="track"></span>
              Market comparison
            </label>
          </div>

          {sections.map((sec) => (
            <section key={sec.id} id={sec.id}>
              <div className="sec-head">
                <h2>{sec.title}</h2>
                <span className="sec-tag">{sec.tag}</span>
              </div>
              <p className="sec-sub">{sec.sub}</p>
              <div className="ticks">
                {sec.ticks.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
              {sec.tiers.map((tier) => (
                <TierRow key={tier.title} tier={tier} />
              ))}
            </section>
          ))}

          <section id="addons">
            <div className="sec-head">
              <h2>Add-On Features</h2>
              <span className="sec-tag">À la carte</span>
            </div>
            <p className="sec-sub">
              Add any of these to a project above. Prices are the same whether it goes
              on a website or in an app; adding it to both at once costs about 40% more
              than one, not double.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="n">Our price</th>
                  <th className="n mkt-only">Market</th>
                </tr>
              </thead>
              <tbody>
                {addons.map((row) => (
                  <tr key={row.feat}>
                    <td className="feat">
                      {row.feat}
                      <span>{row.desc}</span>
                    </td>
                    <td className="n">{row.ours}</td>
                    <td className="n mkt-only">{row.market}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section id="care">
            <div className="sec-head">
              <h2>After Launch</h2>
              <span className="sec-tag">Monthly · cancel anytime</span>
            </div>
            <p className="sec-sub">
              A site or app left alone quietly breaks — expired certificates, outdated
              plugins, forms that stop sending. Every project includes 30 days of free
              fixes; after that, pick a plan or pay as you go at $95/hour.
            </p>
            <div className="plans">
              {plans.map((plan) => (
                <div key={plan.name} className={`plan${plan.pick ? ' pick' : ''}`}>
                  <h4>{plan.name}</h4>
                  <span className="mo">{plan.price}<small>/mo</small></span>
                  <ul>
                    {plan.incl.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <div className="terms">
            <div>
              <h4>How you pay</h4>
              <p>
                40% to start, 30% at design approval, 30% at launch. Fixed fee — the
                number in your proposal is the number on your invoice.
              </p>
            </div>
            <div>
              <h4>What's not included</h4>
              <p>
                Ongoing costs you pay directly: hosting if you're not on a plan, domain
                renewal, Apple ($99/yr) and Google ($25 once) developer accounts, payment
                processing fees (~2.9% + 30¢), and any paid software the project needs.
              </p>
            </div>
            <div>
              <h4>Changes mid-project</h4>
              <p>
                Two rounds of revisions are included at each stage. New features requested
                after we start are quoted separately before any work happens — never billed
                as a surprise.
              </p>
            </div>
            <div>
              <h4>What you own</h4>
              <p>
                All of it. Code, designs, domains, and accounts are in your name from day
                one. If you ever leave, nothing is held hostage.
              </p>
            </div>
          </div>

          <div className="rate-cta">
            <Link to="/#contact" className="btn green">Get your exact quote</Link>
            <span className="fnote" style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
              Free scope call · Reply within one business day
            </span>
          </div>

          <p className="foot">
            Market ranges reflect published 2026 pricing from web and app development
            firms across New York City and northern New Jersey, including small studios,
            mid-market agencies, and industry cost surveys. Ranges are for comparison only
            and will vary by scope. Prices on this card are valid for 30 days from the
            date above.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
};
